import { useState } from 'react';
import { api, type InviteResult } from '../../api/client';
import { Button, Card, Chip, Dialog, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { dateOnly, plural } from '../../lib/format';
import { useData } from '../../lib/useData';
import { ShareLink } from '../tenant/Team';

/** The Stitch team: who can see every customer. */
export function AdminStaff() {
  const { state } = useAuth();
  const data = useData(() => api.staff(), []);
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState<InviteResult | null>(null);
  const [error, setError] = useState('');

  async function act(fn: () => Promise<unknown>) {
    setError('');
    try {
      await fn();
      data.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={data.data ? plural(data.data.staff.filter((s) => !s.disabled).length, 'person', 'people') : 'Stitch Admin'}
        title="Stitch team"
        actions={
          <Button variant="primary" icon="plus" onClick={() => (setInviting(true), setEmail(''), setName(''))}>
            Invite a teammate
          </Button>
        }
      />
      <div className="content">
        <Notice>Everyone here can see and change every customer. Sessions end after 12 hours without use.</Notice>
        {result && <ShareLink result={result} />}
        {error && <Notice tone="crit">{error}</Notice>}
        <Card>
          {data.error ? (
            <LoadError error={data.error} retry={data.reload} />
          ) : !data.data ? (
            <Loading />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Person</th>
                    <th scope="col" className="hide-sm">
                      Last signed in
                    </th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.staff.map((s) => (
                    <tr key={s.id} className={s.disabled ? 'staged removed' : ''}>
                      <td>
                        <span className="cell-stack">
                          <span>
                            {s.name || s.email} {s.id === state?.user?.id && <Chip small>You</Chip>}{' '}
                            {s.disabled && (
                              <Chip small tone="crit">
                                Turned off
                              </Chip>
                            )}
                          </span>
                          <small className="muted">{s.email}</small>
                        </span>
                      </td>
                      <td className="hide-sm">{s.lastSignInAt ? dateOnly(s.lastSignInAt) : <span className="muted">Not yet</span>}</td>
                      <td>
                        <div className="row-actions">
                          {s.id !== state?.user?.id && (
                            <Button size="small" variant="ghost" onClick={() => void act(() => api.setStaffDisabled(s.id, !s.disabled))}>
                              {s.disabled ? 'Turn back on' : 'Turn off'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {data.data.invites.map((i) => (
                    <tr key={i.id} className="staged">
                      <td>
                        <span className="cell-stack">
                          <span>
                            {i.name || i.email}{' '}
                            <Chip small tone="brand">
                              Invited
                            </Chip>
                          </span>
                          <small className="muted">{i.email}</small>
                        </span>
                      </td>
                      <td className="hide-sm muted">Link expires {dateOnly(i.expiresAt)}</td>
                      <td>
                        <div className="row-actions">
                          <Button size="small" variant="ghost" onClick={() => void act(() => api.revokeStaffInvite(i.id))}>
                            Cancel invitation
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      <Dialog
        open={inviting}
        onClose={() => setInviting(false)}
        title="Invite a teammate"
        subtitle="They’ll see every customer."
        foot={
          <>
            <Button variant="ghost" onClick={() => setInviting(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!email.trim()}
              onClick={() =>
                void act(async () => {
                  setResult(await api.inviteStaff(email.trim(), name.trim()));
                  setInviting(false);
                })
              }
            >
              Invite
            </Button>
          </>
        }
      >
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-autofocus />
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      </Dialog>
    </>
  );
}
